import type { ChatArtifactKind } from '@axilon-apps/domains-ontology-ui-state'

import { ARTIFACT_BADGE_LABEL } from '../../pages/ArtifactPage/artifact-strings'

export interface ChatArtifactRef {
  id: string
  title: string
  kind: ChatArtifactKind
  fetchMode: 'snapshot' | 'live'
}

/** Historical document hand-offs remain renderable; new runs write documents directly. */
const WRITE_DOCUMENT_TOOL_NAME = 'write_document'

const DOCUMENT_TOOL_NAMES: ReadonlySet<string> = new Set([
  'add_chat_document',
  'edit_chat_document',
  WRITE_DOCUMENT_TOOL_NAME,
  // Ask's document hand-offs in chats from before `write_document`.
  'create_chat_document',
  'revise_chat_document',
  'fill_template'
])

const ARTIFACT_TOOL_NAMES: ReadonlySet<string> = new Set([
  'add_chat_artifact',
  'edit_chat_artifact',
  'create_chat_artifact',
  'revise_chat_artifact',
  ...DOCUMENT_TOOL_NAMES
])

function recordOf(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  if (typeof value === 'string') {
    try {
      return recordOf(JSON.parse(value))
    } catch {
      return null
    }
  }
  if (Array.isArray(value)) {
    const text = value
      .map((part) => recordOf(part)?.text)
      .filter((part): part is string => typeof part === 'string')
      .join('')
    return text === '' ? null : recordOf(text)
  }
  return null
}

function successfulArtifactOutput(name: string, output: Record<string, unknown>): boolean {
  if (output.error !== undefined) return false
  if (name === 'add_chat_artifact' || name === 'add_chat_document') return output.created === true
  if (name === 'edit_chat_artifact' || name === 'edit_chat_document') {
    return output.updated === true
  }
  if (name === 'fill_template') {
    // Fills from before drafts wrote a library document, named by `doc_id`.
    return (output.created === true || output.updated === true) && output.doc_id === undefined
  }
  return true
}

/** The artifacts one call wrote: `write_document` lists its drafts, other tools return one. */
function artifactRecords(name: string, output: Record<string, unknown>): Record<string, unknown>[] {
  if (name !== WRITE_DOCUMENT_TOOL_NAME) return [output]
  if (!Array.isArray(output.drafts)) return []
  return output.drafts
    .map((draft) => recordOf(draft))
    .filter((draft): draft is Record<string, unknown> => draft !== null)
}

export function collectChatArtifactRefs(
  calls: ReadonlyArray<{ name: string; output: unknown; status?: string }>
): ChatArtifactRef[] {
  const refs: ChatArtifactRef[] = []
  const indexById = new Map<string, number>()
  for (const call of calls) {
    if (call.status === 'running' || !ARTIFACT_TOOL_NAMES.has(call.name)) continue
    const output = recordOf(call.output)
    if (output === null || !successfulArtifactOutput(call.name, output)) continue
    const kind: ChatArtifactKind = DOCUMENT_TOOL_NAMES.has(call.name) ? 'document' : 'dashboard'
    for (const record of artifactRecords(call.name, output)) {
      const id = typeof record.id === 'string' ? record.id.trim() : ''
      if (id === '') continue
      const previousIndex = indexById.get(id)
      const previous = previousIndex === undefined ? undefined : refs[previousIndex]
      const title =
        typeof record.title === 'string' && record.title.trim() !== ''
          ? record.title.trim()
          : (previous?.title ?? ARTIFACT_BADGE_LABEL)
      const fetchMode =
        record.fetch_mode === 'live' || record.fetch_mode === 'snapshot'
          ? record.fetch_mode
          : (previous?.fetchMode ?? 'snapshot')
      const next = { id, title, kind, fetchMode }
      if (previousIndex === undefined) {
        indexById.set(id, refs.length)
        refs.push(next)
      } else {
        refs[previousIndex] = next
      }
    }
  }
  return refs
}

export function previewOwnerByArtifactId(
  turns: ReadonlyArray<{ key: string; refs: readonly ChatArtifactRef[] }>
): Map<string, string> {
  const owner = new Map<string, string>()
  for (const turn of turns) {
    for (const ref of turn.refs) owner.set(ref.id, turn.key)
  }
  return owner
}
